import { isBefore, subDays } from "date-fns";
import { ko } from "date-fns/locale";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import DatePicker, { CalendarContainer } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styled from "styled-components";
import { ReactComponent as IconCalendar } from "../Images/icon_calendar.svg";
import AlarmSimpleOk from "../Modals/AlarmSimpleOk";
import { postJson } from "../Utils/apiUtils";

const EventDateOption = ({
  startTitle,
  endTitle,
  width,
  fontSize,
  gap,
  height,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  type,
  readOnly = false,
}) => {
  const [tempValueForSearch, setTempValueForSearch] = useState(
    type === "search" ? true : false
  );
  // 임시로 선택된 날짜 (적용 버튼 누르기 전)
  const [tempStartDate, setTempStartDate] = useState(
    startDate ? startDate : new Date()
  );
  const [tempEndDate, setTempEndDate] = useState(
    endDate ? endDate : new Date()
  );

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [calendarOpen2, setCalendarOpen2] = useState(false);
  const [showAlarm, setShowAlarm] = useState(false);
  const [excludedIntervals, setExcludedIntervals] = useState([
    { start: "", end: "" },
  ]);

  const startDatePickerRef = useRef(null);
  const endDatePickerRef = useRef(null);

  const applyStartDateChange = () => {
    if (readOnly) {
      return;
    }

    setStartDate(tempStartDate);
    if (isBefore(endDate, tempStartDate)) {
      setEndDate(tempStartDate);
    }
    startDatePickerRef.current.setOpen(false);
  };

  const applyEndDateChange = () => {
    if (readOnly) {
      return;
    }

    setEndDate(tempEndDate);
    if (isBefore(tempEndDate, startDate)) {
      setStartDate(tempEndDate);
    }
    endDatePickerRef.current.setOpen(false);
  };

  useEffect(() => {
    if (!tempValueForSearch) {
      setTempStartDate(startDate);
      setTempEndDate(endDate);
    }
  }, [startDate, endDate]);

  // 유효한 날짜 가져오기
  const params = useParams().id;

  const getEventList = async () => {
    if (readOnly) {
      return;
    }

    try {
      const body = {
        page: 1,
        offset: 10,
        searchTitle: "",
      };

      const data = await postJson("/dashboard/event/getList", body);
      // 1. 수정 중인 경우, 해당 대회 날짜 제외
      if (data.code === 200 && Array.isArray(data.data.rows)) {
        const now = new Date();

        let filteredIntervals = data.data.rows
          .filter((event) => event.startTime && event.endTime)
          .map((event) => ({
            id: event.seasonId,
            start: new Date(event.startTime),
            end: new Date(event.endTime),
          }));

        if (params) {
          filteredIntervals = filteredIntervals.filter((interval) => {
            return String(interval.id) !== String(params);
          });
        }

        // 2. 오늘 이전은 제외
        const validIntervals = filteredIntervals.filter(
          ({ end }) => end >= now
        );

        setExcludedIntervals(
          validIntervals.map(({ start, end }) => ({ start, end }))
        );
      }
    } catch (error) {
      console.log("이벤트대회 리스트 가져오기 중 에러:", error);
      throw error;
    }
  };

  const handleStartCalendarOpen = () => {
    if (readOnly) {
      return;
    }

    setCalendarOpen(true);
    getEventList();
  };

  const isDateExcluded = (date) => {
    return excludedIntervals.some(
      (range) => date >= subDays(range.start, 1) && date <= range.end
    );
  };

  const isRangeOverlapping = (start, end) => {
    return excludedIntervals.some((range) => {
      return start <= range.end && end >= range.start;
    });
  };

  return (
    <div>
      <div style={{ display: "flex", gap: gap }}>
        <div>
          {startTitle && (
            <p style={{ fontFamily: "notoSans-medium" }}>{startTitle}</p>
          )}
          <DatePickerWrapper>
            <DatePicker
              ref={startDatePickerRef}
              excludeDateIntervals={excludedIntervals}
              customInput={
                <CustomInput
                  $isClicked={calendarOpen}
                  width={width}
                  fontSize={fontSize}
                  height={height}
                  readOnly={readOnly}
                  onChange={
                    type === "search"
                      ? (date) => {
                          setTempStartDate(date);
                        }
                      : (date) => {
                          !isBefore(date, new Date()) && setTempStartDate(date);
                          setTempValueForSearch(false);
                        }
                  }
                />
              }
              selected={tempStartDate}
              onChange={
                type === "search"
                  ? (date) => {
                      setTempStartDate(date);
                    }
                  : (date) => {
                      !isBefore(date, new Date()) && setTempStartDate(date);
                      setTempValueForSearch(false);
                    }
              }
              selectsStart
              startDate={tempValueForSearch ? new Date() : tempStartDate}
              endDate={tempStartDate}
              dateFormat="yyyy-MM-dd"
              dayClassName={
                type === "search"
                  ? null
                  : (date) =>
                      isBefore(date, new Date()) || isDateExcluded(date)
                        ? "disabled-day"
                        : "enabled-day"
              }
              popperContainer={CalendarContainer}
              locale={ko}
              onCalendarOpen={handleStartCalendarOpen}
              onCalendarClose={() => setCalendarOpen(false)}
              shouldCloseOnSelect={false}
              disabled={readOnly}
            >
              <div className="datepicker-footer">
                <div
                  className="datepicker-applyBtn"
                  onClick={applyStartDateChange}
                >
                  적용
                </div>
              </div>
            </DatePicker>
          </DatePickerWrapper>
        </div>

        <div>
          {endTitle && (
            <p style={{ fontFamily: "notoSans-medium" }}>{endTitle}</p>
          )}
          <DatePickerWrapper>
            <DatePicker
              ref={endDatePickerRef}
              excludeDateIntervals={excludedIntervals}
              customInput={
                <CustomInput
                  $isClicked={calendarOpen2}
                  width={width}
                  fontSize={fontSize}
                  height={height}
                  readOnly={readOnly}
                  onChange={(date) => {
                    setTempEndDate(date);
                    setTempValueForSearch(false);
                  }}
                />
              }
              selected={tempValueForSearch ? new Date() : endDate}
              onChange={(date) => {
                const start = new Date(tempStartDate);
                const end = new Date(date);
                if (isBefore(end, start)) return;
                if (isRangeOverlapping(start, end)) {
                  setShowAlarm(true);
                  return;
                }
                setTempEndDate(date);
                setTempValueForSearch(false);
              }}
              selectsEnd
              startDate={tempValueForSearch ? new Date() : startDate}
              endDate={tempEndDate}
              dateFormat="yyyy-MM-dd"
              dayClassName={(date) =>
                isBefore(
                  date,
                  startDate !== null
                    ? new Date(startDate).getTime() - 24 * 60 * 60 * 1000
                    : startDate
                ) || isDateExcluded(date)
                  ? "disabled-day"
                  : "enabled-day"
              }
              popperContainer={CalendarContainer}
              locale={ko}
              onCalendarOpen={() => setCalendarOpen2(true)}
              onCalendarClose={() => setCalendarOpen2(false)}
              shouldCloseOnSelect={false}
              disabled={readOnly}
            >
              <div className="datepicker-footer">
                <div
                  className="datepicker-applyBtn"
                  onClick={() => {
                    applyEndDateChange();
                  }}
                >
                  적용
                </div>
              </div>
            </DatePicker>
          </DatePickerWrapper>
        </div>
      </div>
      {showAlarm && (
        <AlarmSimpleOk
          title={"진행 중인 대회가 있습니다"}
          description={
            "이미 등록된 대회와 겹치는 날짜입니다. 다른 날짜를 선택해주세요."
          }
          handleClickX={() => setShowAlarm(false)}
        />
      )}
    </div>
  );
};

const CustomInput = forwardRef(
  (
    { value, onClick, isClicked, width, fontSize, height, onChange, readOnly },
    ref
  ) => {
    return (
      <InputWrapper
        onClick={readOnly ? undefined : onClick}
        ref={ref}
        $isClicked={isClicked}
        width={width}
        fontSize={fontSize}
        height={height}
        $readOnly={readOnly}
      >
        <input
          type="text"
          value={value}
          onChange={onChange} // 사용자 입력
          onFocus={readOnly ? undefined : onClick} // focus => calendar open
          placeholder="0000-00-00"
          style={{
            border: "none",
            outline: "none",
            fontSize: fontSize || "18px",
            width: "100%",
            backgroundColor: "transparent",
            cursor: readOnly ? "default" : "pointer",
          }}
          className="placeholdercolor"
          readOnly={readOnly}
        />
        <div>
          <IconCalendar style={{ verticalAlign: "bottom" }} />
        </div>
      </InputWrapper>
    );
  }
);

const InputWrapper = styled.div`
  width: ${(props) => (props.width ? props.width : "290px")};
  height: ${(props) => (props.height ? props.height : "58px")};
  border-radius: 10px;
  background-color: #ffffff;
  padding: 0 20px;
  box-shadow: inset 0 0 0 1px
    ${(props) => (props.$isClicked ? "#1167ed" : "#e4e4e4")};
  margin-top: 8px;
  cursor: ${(props) => (props.$readOnly ? "default" : "pointer")};
  display: flex;
  align-items: center;
  > div {
    width: 24px;
    height: 24px;
    margin-top: 2px;
    margin-left: auto;
  }
  > p {
    font-size: ${(props) => (props.fontSize ? props.fontSize : "18px")};
  }
`;

const DatePickerWrapper = styled.div`
  .react-datepicker {
    width: 330px;
    height: auto;
    border-radius: 12px;
    border: none;
    box-shadow: inset 0 0 0 1px #e4e4e4, 0 4px 20px rgba(0, 0, 0, 0.05);
  }
  .react-datepicker-popper .react-datepicker__triangle {
    display: none;
  }
  .react-datepicker__current-month {
    font-family: "notoSans-bold";
    font-size: 16px;
    color: #09090b;
    margin-top: 6px;
  }
  .react-datepicker__day-names {
    width: 298px;
    height: 40px;
    margin: 0;
    margin-top: 25px;
  }
  .react-datepicker__day-name {
    font-family: "notoSans-bold";
    font-size: 13px;
    color: #757575;
    margin: 7px;
  }
  .react-datepicker__header {
    width: 298px;
    height: 37px;
    padding: 12px 16px;
    background-color: #ffffff;
    box-shadow: inset 0 0 0 1px #e4e4e4;
    border: none;
    border-radius: 10px 10px 0 0;
  }

  .react-datepicker__navigation {
    width: 40px;
    height: 38px;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px #e4e4e4;
    margin: 9px;
  }
  .react-datepicker__navigation-icon {
    margin-top: 8px;
    &::before {
      border-color: #6d6d6d;
      border-width: 1.5px 1.5px 0 0;
    }
    &:focus {
      outline: none;
    }
  }
  .react-datepicker__navigation-icon--previous {
    margin-right: 2px;
  }
  .react-datepicker__navigation-icon--next {
    margin-left: 2px;
  }
  .react-datepicker__month-container {
    height: auto;
  }
  .react-datepicker__month {
    margin: 0;
    margin-top: 60px;
  }
  .react-datepicker__day {
    font-family: "notoSans-medium";
    font-size: 14px;
    margin: 7px;
  }
  .enabled-day {
    color: #3f3f46;
    border-radius: 50%;
    &:hover {
      background-color: #1167ed !important;
      border-radius: 50% !important;
      color: #ffffff;
    }
  }
  .disabled-day {
    color: #b0b0b0 !important;
    background-color: transparent;
    pointer-events: none;
  }
  .react-datepicker__day--selected {
    background-color: #1167ed;
    color: #ffffff;
    border-radius: 50% !important;
    outline: none;
  }

  .react-datepicker__day--in-selecting-range {
    background-color: #1167ed;
    background-color: transparent !important;
    color: #ffffff;
    border-radius: 50%;
    outline: none;
    &:hover {
      background-color: #1167ed !important;
      color: #ffffff;
      border-radius: 50%;
    }
  }

  .react-datepicker__day--in-range {
    background-color: #1167ed !important;
    color: #ffffff !important;
    border-radius: 50%;
    outline: none;
  }

  .react-datepicker__day--outside-month {
    color: #b0b0b0;
    &:hover {
      color: #ffffff;
      border-radius: 50%;
    }
  }

  .react-datepicker__day--today {
    &:hover {
      border-radius: 50%;
    }
  }
  .react-datepicker__day--in-selecting-range {
    background-color: transparent;
    color: #3f3f46;
  }
  .react-datepicker__day--selecting-range-end {
    background-color: #1167ed;
    color: #3f3f46;
    border-radius: 50%;
  }
  .react-datepicker__day--selecting-range-start {
    background-color: #1167ed;
    color: #3f3f46;
    border-radius: 50%;
  }

  .react-datepicker__day--keyboard-selected {
    background-color: transparent;
  }

  .datepicker-footer {
    width: 298px;
    height: 44px;
    padding: 16px;
    border-top: 1px solid #f4f4f5;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: -10px;
  }
  .datepicker-applyBtn {
    width: 298px;
    height: 44px;
    border-radius: 6px;
    background-color: #1167ed;
    display: flex;
    justify-content: center;
    align-items: center;
    font-family: "notoSans-bold";
    font-size: 14px;
    color: #ffffff;
    cursor: pointer;
  }
`;

export default EventDateOption;
